import posthog from 'posthog-js'
import { useEffect, useState } from 'react'
import {
  DEFAULT_LANDING_HERO_VARIANT,
  type LandingHeroVariant,
} from '../routes/landing-hero-variants'

const FLAG_KEY = 'landing-hero-variant'

/**
 * How long the hero copy is allowed to wait on PostHog before falling back to
 * `DEFAULT_LANDING_HERO_VARIANT`. Flags normally arrive from the bootstrap
 * cache at 0ms (see `initWebAnalytics`); this only bites a first-ever visitor
 * on a slow network, where showing SOMETHING beats an empty hero.
 */
const RESOLVE_TIMEOUT_MS = 400

export interface LandingHeroVariantState {
  variant: LandingHeroVariant
  /** false = still waiting on the flag; the hero copy must stay invisible. */
  resolved: boolean
}

function isVariant(value: unknown): value is LandingHeroVariant {
  return value === 'control' || value === 'reel-first'
}

/** `?hv=control|reel-first` - a QA forcing pin, not an experiment arm. */
function readQaOverride(): LandingHeroVariant | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = new URLSearchParams(window.location.search).get('hv')
    return isVariant(raw) ? raw : null
  } catch {
    return null
  }
}

function initialState(): LandingHeroVariantState {
  const override = readQaOverride()
  return override
    ? { variant: override, resolved: true }
    : { variant: DEFAULT_LANDING_HERO_VARIANT, resolved: false }
}

/**
 * Resolves the hero variant. A `?hv=` override wins immediately and deliberately
 * SKIPS flag evaluation so a QA pin never records an experiment exposure;
 * otherwise the PostHog flag decides (reading it via `getFeatureFlag` is what
 * records the exposure).
 */
export function useLandingHeroVariant(): LandingHeroVariantState {
  const [state, setState] = useState<LandingHeroVariantState>(initialState)

  useEffect(() => {
    if (state.resolved) return

    let settled = false
    let unsubscribe: (() => void) | undefined
    let timer: number | undefined

    const settle = (variant: LandingHeroVariant, viaTimeout: boolean) => {
      if (settled) return
      settled = true
      if (timer !== undefined) window.clearTimeout(timer)
      if (viaTimeout) {
        try {
          // Marks every event from this session so a skewed experiment read
          // caused by slow flags is visible in the analysis, not invisible.
          // SESSION-scoped on purpose: `register` persists to localStorage
          // forever, so one slow first visit would permanently inflate the
          // measured fallback rate for that browser.
          posthog.register_for_session({ hero_variant_fallback: true })
        } catch {
          // swallow - analytics must never break the page
        }
      }
      setState({ variant, resolved: true })
    }

    try {
      unsubscribe = posthog.onFeatureFlags((_flags, _variants, context) => {
        // A failed flag load fires this callback too. Settling on it would lock
        // in the default AND drop the subscription, so a load that recovers inside
        // the timeout window could never be picked up - and the fallback would
        // be invisible in the analysis. Let the timeout own that case instead.
        if (context?.errorsLoading) return
        let value: string | boolean | undefined
        try {
          value = posthog.getFeatureFlag(FLAG_KEY)
        } catch {
          value = undefined
        }
        settle(isVariant(value) ? value : DEFAULT_LANDING_HERO_VARIANT, false)
      })
    } catch {
      settle(DEFAULT_LANDING_HERO_VARIANT, false)
      return
    }

    if (settled) {
      // Bootstrapped flags fire the callback synchronously during registration,
      // so the subscription handle only exists now - drop it and skip the timer.
      unsubscribe()
      unsubscribe = undefined
    } else {
      timer = window.setTimeout(() => settle(DEFAULT_LANDING_HERO_VARIANT, true), RESOLVE_TIMEOUT_MS)
    }

    return () => {
      settled = true
      if (timer !== undefined) window.clearTimeout(timer)
      unsubscribe?.()
    }
  }, [state.resolved])

  return state
}
